import * as PIXI from 'pixi.js';
import { Ticker } from 'pixi.js';
import { BlendOperation, DisposeOperation } from './apngDecoder/chunks/frameControlChunkView';
import { AnimatedPngMetadata, FrameRenderingInfo } from './apngDecoder/transformers/frameToPngTransformer';

import type { Container, IDestroyOptions, Texture } from 'pixi.js';

export type PngFrameTexture = {
    metadata: FrameRenderingInfo,
    texture: PIXI.Texture,
};

export interface StreamableAnimatedSpriteOptions
{
    /**
     * Whether to use Ticker.shared to auto update animation time.
     */
    autoUpdate?: boolean,
    /**
     * Cache every N-th frame to increase rendering speed at cost of memory
     */
    framesCacheInterval?: number
}

export default class StreamableAnimatedSprite extends PIXI.Sprite
{
    /**
     * The speed that the AnimatedSprite will play at. Higher is faster, lower is slower.
     * @default 1
     */
    public animationSpeed: number;

    /**
     * User-assigned function to call when an AnimatedSprite finishes playing.
     * @example
     * animation.onComplete = () => {
     *     // Finished!
     * };
     */
    public onComplete?: () => void;

    /**
     * User-assigned function to call when an AnimatedSprite changes which texture is being rendered.
     * @example
     * animation.onFrameChange = () => {
     *     // Updated!
     * };
     */
    public onFrameChange?: (currentFrame: number) => void;

    /**
     * User-assigned function to call when `loop` is true, and an AnimatedSprite is played and
     * loops around to start again.
     * @example
     * animation.onLoop = () => {
     *     // Looped!
     * };
     */
    public onLoop?: (loopNumber: number) => void;
    public readonly loadComplete: Promise<void>;
    private _loopNumber = 0;
    private _durations: number[];
    private _animationInfo: ({ width: number, height: number }) | undefined;
    /**
     * `true` if the instance is currently connected to Ticker.shared to auto update animation time.
     * @default false
     */
    private _isConnectedToTicker: boolean;
    /** Elapsed time since animation has been started, used internally to display current texture. */
    private _currentTime: number;
    /** The texture index that was displayed last time. */
    private _previousFrame: number = -1;
    private _isLoading: boolean;
    private _abortLoading: AbortController;

    /**
     * @param textureStream - Stream to load textures from
     * @param metadataFactory - Factory to obtain metadata from during the loading
     * @param options - Options
     */
    constructor(
        textureStream: ReadableStream<PngFrameTexture>,
        metadataFactory: () => AnimatedPngMetadata,
        options?: StreamableAnimatedSpriteOptions,
    )
    {
        super();

        this._durations = [];
        this._framesCacheInterval = options?.framesCacheInterval ?? 10;

        this._autoUpdate = options?.autoUpdate ?? true;
        this._isConnectedToTicker = false;

        this.animationSpeed = 1;
        this._animationInfo = undefined;
        this.onComplete = undefined;
        this.onFrameChange = undefined;
        this.onLoop = undefined;

        this._currentTime = 0;

        this._playing = false;
        this._previousFrame = -1;

        this._isLoading = true;
        this._abortLoading = new AbortController();

        this.loadComplete = this.startLoading(textureStream, metadataFactory, this._abortLoading.signal);
    }

    private _playTimes: number | undefined;

    /**
     * Times to repeat the animation. 0 - infinite
     */
    get playTimes(): number
    {
        return this._playTimes ?? 0;
    }

    set playTimes(times: number)
    {
        if (times < 0)
        {
            throw new Error('Invalid number of times to play the animation');
        }
        this._playTimes = times;
    }

    private _framesCacheInterval: number;

    get framesCacheInterval(): number
    {
        return this._framesCacheInterval;
    }

    set framesCacheInterval(interval: number)
    {
        this._framesCacheInterval = interval;
        for (let i = 0; i < this.frames.length; i++)
        {
            this.frames[i].cacheAsBitmap = this.isCachedFrame(i);
        }
    }

    private _playing: boolean;

    /**
     * Whether animation is currently playing.
     * @readonly
     */
    get playing(): boolean
    {
        return this._playing;
    }

    /** Frame containers of the animation. */
    get frames(): readonly Container[]
    {
        return this.children as Container[];
    }

    /**
     * `true` uses Ticker.shared to auto update animation time.
     * @default true
     */
    private _autoUpdate: boolean;

    /** Whether to use Ticker.shared to auto update animation time. */
    get autoUpdate(): boolean
    {
        return this._autoUpdate;
    }

    set autoUpdate(value: boolean)
    {
        if (value !== this._autoUpdate)
        {
            this._autoUpdate = value;

            if (!this._autoUpdate && this._isConnectedToTicker)
            {
                Ticker.shared.remove<StreamableAnimatedSprite>(this.onTick, this);
                this._isConnectedToTicker = false;
            }
            else if (this._autoUpdate && !this._isConnectedToTicker && this._playing)
            {
                Ticker.shared.add<StreamableAnimatedSprite>(this.onTick, this);
                this._isConnectedToTicker = true;
            }
        }
    }

    /**
     * The total number of frames in the animation currently loaded.
     * @readonly
     * @default 0
     */
    get totalFrames(): number
    {
        return this.frames.length;
    }

    /** The animation's current frame index. */
    get currentFrame(): number
    {
        if (!this.frames.length)
        {
            return -1;
        }

        let currentFrame = Math.floor(this._currentTime) % this.frames.length;

        if (currentFrame < 0)
        {
            currentFrame += this.frames.length;
        }

        return currentFrame;
    }

    set currentFrame(value: number)
    {
        if (this._isLoading && value > this.totalFrames - 1)
        {
            throw new Error(`Frame ${value} hasn't been loaded yet (loaded frames: ${this.totalFrames}).`);
        }

        if (value < 0 || value > this.totalFrames - 1)
        {
            throw new Error(`Invalid frame index value ${value}, `
                + `expected to be between 0 and totalFrames ${this.totalFrames}.`);
        }

        const previousFrame = this.currentFrame;

        this._currentTime = value;

        if (previousFrame !== this.currentFrame)
        {
            this._updateFrame();
        }
    }

    /** Stops the animation. */
    public stop(): void
    {
        this._loopNumber = 0;
        if (!this._playing)
        {
            return;
        }

        this._playing = false;
        if (this._autoUpdate && this._isConnectedToTicker)
        {
            Ticker.shared.remove<StreamableAnimatedSprite>(this.onTick, this);
            this._isConnectedToTicker = false;
        }
    }

    /** Plays the animation. */
    public play(): void
    {
        if (this._playing)
        {
            return;
        }

        this._playing = true;
        if (this._autoUpdate && !this._isConnectedToTicker)
        {
            Ticker.shared.add<StreamableAnimatedSprite>(this.onTick, this, PIXI.UPDATE_PRIORITY.HIGH);
            this._isConnectedToTicker = true;
        }
    }

    /**
     * Stops the animation and goes to a specific frame.
     * @param frameNumber - Frame index to stop at.
     */
    public gotoAndStop(frameNumber: number): void
    {
        this.stop();
        this.currentFrame = frameNumber;
    }

    /**
     * Goes to a specific frame and begins playing the animation.
     * @param frameNumber - Frame index to start at.
     */
    public gotoAndPlay(frameNumber: number): void
    {
        this.currentFrame = frameNumber;
        this.play();
    }

    /**
     * Updates the object transform for rendering.
     * @param deltaTime - the delta time to update the object.
     */
    public onTick(deltaTime: number): void
    {
        if (!this._playing)
        {
            return;
        }

        // Stop animation until the next frame is loaded
        if (this._isLoading && (this.animationSpeed <= 0 || this.currentFrame >= this.totalFrames - 1))
        {
            return;
        }

        // Calculate elapsed time based on ticker's deltaTime and animation speed.
        const elapsed = this.animationSpeed * deltaTime;
        const previousFrame = this.currentFrame;

        // Calculate the lag for the current frame based on the current time.
        let lag = this._currentTime % 1 * this._durations[this.currentFrame];

        // Adjust the lag based on elapsed time.
        lag += elapsed / 60 * 1000;

        // If the lag is negative, adjust the current time and the lag.
        while (lag < 0)
        {
            this._currentTime--;
            lag += this._durations[this.currentFrame];
        }

        const sign = Math.sign(this.animationSpeed * deltaTime);

        // Floor the current time to get a whole number frame.
        this._currentTime = Math.floor(this._currentTime);

        // Adjust the current time and the lag until the lag is less than the current frame's duration.
        while (lag >= this._durations[this.currentFrame])
        {
            lag -= this._durations[this.currentFrame] * sign;
            this._currentTime += sign;
        }

        // Adjust the current time based on the lag and current frame's duration.
        this._currentTime += lag / this._durations[this.currentFrame];

        const loop = this.canLoop();

        // Handle scenarios when animation reaches the start or the end.
        if (this._currentTime < 0 && !loop)
        {
            // If the animation shouldn't loop and it reaches the start, go to the first frame.
            this.gotoAndStop(0);

            // If there's an onComplete callback, call it.
            if (this.onComplete)
            {
                this.onComplete();
            }
        }
        else if (this._currentTime >= this.frames.length && !loop)
        {
            // If the animation shouldn't loop and it reaches the end, go to the last frame.
            this.gotoAndStop(this.frames.length - 1);

            // If there's an onComplete callback, call it.
            if (this.onComplete)
            {
                this.onComplete();
            }
        }
        else if (previousFrame !== this.currentFrame)
        {
            // If the current frame is different from the last update, handle loop scenarios.
            if (loop)
            {
                if ((this.animationSpeed > 0 && this.currentFrame < previousFrame)
                    || (this.animationSpeed < 0 && this.currentFrame > previousFrame))
                {
                    this.loop();
                }
            }

            if (this.canLoop())
            {
                // Update the texture for the current frame.
                this._updateFrame();
            }
        }
    }

    private canLoop()
    {
        return !this._playTimes || this._loopNumber < this._playTimes;
    }

    abortLoading(reason = 'Aborted loading by user')
    {
        this._abortLoading.abort(reason);

        return this.loadComplete;
    }

    destroy(options?: IDestroyOptions | boolean)
    {
        this._abortLoading.abort('Sprite is destroyed');

        if (this._isLoading)
        {
            throw new Error('Attempted to destroy loading sprite');
        }

        this.stop();

        super.destroy(options);

        this._durations = [];
        this.onComplete = undefined;
        this.onFrameChange = undefined;
        this.onLoop = undefined;
    }

    setTransform(
        x?: number,
        y?: number,
        scaleX?: number,
        scaleY?: number,
        rotation?: number,
        skewX?: number,
        skewY?: number,
        pivotX?: number,
        pivotY?: number
    ): this
    {
        return super.setTransform(x, y, scaleX, scaleY, rotation, skewX, skewY, pivotX, pivotY) as this;
    }

    private loop()
    {
        this._loopNumber++;
        // If the animation loops, and there's an onLoop callback, call it.
        this.onLoop?.(this._loopNumber);
    }

    private isCachedFrame(frameNumber: number)
    {
        return (frameNumber + 1) % (this._framesCacheInterval + 1) === 0;
    }

    private createMask(frame: { x: number, y: number, width: number, height: number }): PIXI.Container
    {
        const mask = new PIXI.Graphics();

        if (!this._animationInfo)
        {
            throw new Error('Animation info is missing');
        }

        mask.beginFill(0xffffff);
        mask.drawRect(0, 0, this._animationInfo.width, this._animationInfo.height);
        mask.beginHole();
        mask.drawRect(frame.x, frame.y, frame.width, frame.height);
        mask.endHole();
        mask.endFill();

        return mask as PIXI.Container;
    }

    private cloneContainer(from: PIXI.Container, to: PIXI.Container): void
    {
        for (const child of from.children)
        {
            if (child instanceof PIXI.Sprite)
            {
                const sprite = PIXI.Sprite.from(child.texture);

                sprite.name = child.name;
                sprite.x = child.x;
                sprite.y = child.y;

                to.addChild(sprite);
            }
            else if (child instanceof PIXI.Container)
            {
                const toContainer = new PIXI.Container();

                toContainer.name = child.name;
                if (child.mask)
                {
                    const frameMask = (child.mask as PIXI.Graphics).clone();

                    toContainer.mask = frameMask as Container;
                    toContainer.addChild(frameMask);
                }
                toContainer.x = child.x;
                toContainer.y = child.y;
                this.cloneContainer(child as PIXI.Container, toContainer);

                to.addChild(toContainer);
            }
            else
            {
                throw new Error(`Copying of child type ${child.constructor.name} is not supported`);
            }
        }
    }

    /** Updates the displayed texture to match the current frame index. */
    private _updateFrame(): void
    {
        const currentFrame = this.currentFrame;

        if (this._previousFrame === currentFrame)
        {
            return;
        }

        this.frames[this.currentFrame].visible = true;
        if (this._previousFrame >= 0)
        {
            this.frames[this._previousFrame].visible = false;
        }

        this._previousFrame = currentFrame;

        if (this.onFrameChange)
        {
            this.onFrameChange(this.currentFrame);
        }
    }

    private addFrame(
        texture: Texture,
        frameMetadata: FrameRenderingInfo,
        currentFrameContainer = new PIXI.Container()
    )
    {
        if (!frameMetadata)
        {
            throw new Error('Frame metadata is required');
        }
        this._durations.push(frameMetadata.delayNumerator / frameMetadata.delayDenominator * 1000);

        const currentFrame = frameMetadata.frameNumber;
        const currentSprite = PIXI.Sprite.from(texture);

        let nextFrameContainer: PIXI.Container = new PIXI.Container();

        if (frameMetadata.dispose === DisposeOperation.PREVIOUS && currentFrame > 0)
        {
            this.cloneContainer(currentFrameContainer, nextFrameContainer);
        }

        currentSprite.name = `frame_${currentFrame}`;
        currentSprite.x = frameMetadata.x;
        currentSprite.y = frameMetadata.y;

        const frame = new PIXI.Rectangle(currentSprite.x, currentSprite.y, currentSprite.width, currentSprite.height);

        currentFrameContainer.name = `frame_${currentFrame}_container`;
        currentFrameContainer.cacheAsBitmap = this.isCachedFrame(currentFrame);

        if (currentFrame > 0)
        {
            currentFrameContainer.name = `wrapped_frames_before_${currentFrame}`;
            currentFrameContainer = this.wrapPreviousFrame(
                currentFrameContainer,
                frameMetadata.blend === BlendOperation.SOURCE ? frame : undefined
            );
        }

        currentFrameContainer.addChild(currentSprite);
        currentFrameContainer.visible = false;
        this.addChild(currentFrameContainer);

        this._updateFrame();

        if (frameMetadata.dispose !== DisposeOperation.PREVIOUS || currentFrame <= 0)
        {
            this.cloneContainer(currentFrameContainer, nextFrameContainer);
        }

        if (frameMetadata.dispose === DisposeOperation.BACKGROUND)
        {
            nextFrameContainer.name = `masked_frames_before_${currentFrame - 1}`;
            nextFrameContainer = this.wrapPreviousFrame(nextFrameContainer, frame);
        }

        return nextFrameContainer;
    }

    private wrapPreviousFrame(
        previousFrame: PIXI.Container,
        mask?: PIXI.Rectangle
    ): PIXI.Container
    {
        // TODO: (optimize) Check complete replacement case
        const container = new PIXI.Container();

        container.name = previousFrame.name;

        if (mask)
        {
            const maskContainer = this.createMask(mask);

            previousFrame.mask = maskContainer;
            previousFrame.addChild(maskContainer);
        }

        container.addChild(previousFrame);

        return container;
    }

    private async startLoading(
        textureStream: ReadableStream<PngFrameTexture>,
        getAnimationMetadata: () => AnimatedPngMetadata,
        abortSignal: AbortSignal,
    )
    {
        const texturesReader = textureStream.getReader();

        try
        {
            let currentFrameContainer: PIXI.Container | undefined;

            while (true)
            {
                const { done, value } = await texturesReader.read();

                if (done || abortSignal.aborted)
                {
                    break;
                }

                const { metadata, texture } = value;
                const loadedFramesMetadata = getAnimationMetadata();

                this._animationInfo ??= {
                    width: loadedFramesMetadata.width,
                    height: loadedFramesMetadata.height,
                };
                this._playTimes ??= loadedFramesMetadata.playTimes;

                currentFrameContainer = this.addFrame(texture, metadata, currentFrameContainer);
            }
        }
        finally
        {
            texturesReader.releaseLock();
            this._isLoading = false;

            if (abortSignal.aborted)
            {
                await textureStream.cancel(abortSignal.reason);
            }
        }
    }
}
